import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import type { RunDetail, RunStep } from "./types";

/** What staff asked for: skip the step the run is at, mark or unmark a later step, or move the run to a step. */
export type RunAction = { kind: "skip_now" | "mark" | "unmark" | "move"; step: RunStep };

const sends = (s?: RunStep) => !!s && (s.type === "email" || s.type === "sign_in_link");

/** "step 4: Email: Second email" or "the end (the automation finishes for them)". */
function stepText(steps: RunStep[], key: string): string {
  if (key === "end") return "the end (the automation finishes for them)";
  const s = steps.find((x) => x.key === key);
  return s ? `step ${s.number}: ${s.label}` : `step ${key}`;
}

/** The confirm wording for an action, in plain words with the email subject, and the subject it promises to send
 *  right away (so the server's own "this sends an email" check doesn't ask a second time for the same email). */
export function confirmText(a: RunAction, steps: RunStep[], who: string, paused: boolean): { title: string; body: string; button: string; promised?: string } {
  const s = a.step;
  const email = s.subject ? `the email “${s.subject}”` : "";
  const Who = who.charAt(0).toUpperCase() + who.slice(1); // at the start of a sentence ("This person won't...")
  switch (a.kind) {
    case "skip_now": {
      const next = steps.find((x) => x.key === s.next);
      const nextSends = !paused && sends(next) && !next?.skip ? next?.subject || next?.label : undefined;
      const parts = [
        email ? `${Who} won't get ${email}.` : "Nothing from this step is sent or changed.",
        paused ? `The run is paused: when it is resumed it goes on from ${stepText(steps, s.next)}.` : `They move on right away to ${stepText(steps, s.next)}.`,
      ];
      if (nextSends) parts.push(`That step emails them now: “${nextSends}”.`);
      return { title: `Skip “${s.label}” for ${who}?`, body: parts.join(" "), button: nextSends ? "Skip it and send the next email" : "Skip this step", promised: nextSends };
    }
    case "mark":
      return {
        title: `Skip “${s.label}” for ${who} when they get there?`,
        body: `${email ? `${Who} won't get ${email}. ` : ""}Their run passes over this step and goes on to ${stepText(steps, s.next)}. Everything before it happens as usual. Only this person is affected.`,
        button: "Skip it for this person",
      };
    case "unmark":
      return {
        title: `Don't skip “${s.label}” for ${who}?`,
        body: `${Who} gets this step${s.subject ? ` (“${s.subject}”)` : ""} as usual when they reach it.`,
        button: "Don't skip it",
      };
    case "move": {
      const now = !paused && sends(s) && s.skip !== "everyone" ? s.subject || s.label : undefined;
      return {
        title: `Move ${who} to “${s.label}”?`,
        body: paused
          ? "The run is paused: the step runs when it is resumed. Waits start over from then."
          : `The step runs right away${now ? ` and emails them “${now}” now` : ""}. Waits start over from now.`,
        button: now ? "Move and send" : "Move here",
        promised: now,
      };
    }
  }
}

function errorText(e: unknown) {
  return e instanceof ApiError ? e.message : String(e);
}

/** "Skip or move" for one person's run: skip the step they're at, skip a later step for them, or move them to a step.
 *  Each choice asks first in plain words, with the email subject being skipped or sent. */
export function RunSkipDialog({ runId, who, open, onOpenChange }: { runId: number; who: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<RunAction | null>(null);
  const [sendCheck, setSendCheck] = useState<{ action: RunAction; subject: string } | null>(null);
  const [error, setError] = useState("");
  const q = useQuery({ queryKey: ["automation-run", runId], queryFn: () => api<RunDetail>(`/api/v1/automation-enrollments/${runId}`), enabled: open });
  const steps = q.data?.steps ?? [];
  const p = q.data?.position;
  const paused = p?.status === "paused" || p?.automation_status === "paused";
  const run = useMutation({
    mutationFn: async ({ action, confirm, promised }: { action: RunAction; confirm: boolean; promised?: string }) => {
      const url = action.kind === "move" ? `/api/v1/automation-enrollments/${runId}/move` : `/api/v1/automation-enrollments/${runId}/skip`;
      const body: Record<string, unknown> =
        action.kind === "move" ? { step: action.step.key } : action.kind === "skip_now" ? {} : { step: action.step.key, ...(action.kind === "unmark" ? { undo: true } : {}) };
      try {
        return await api(url, { json: confirm ? { ...body, confirm: true } : body });
      } catch (e) {
        // Moving on onto an email sends it now: the CRM asks first (go-live audit H-2). When the confirm already named
        // that same email, the click was the OK; otherwise ask once more with the subject the CRM found.
        if (confirm || !(e instanceof ApiError) || e.code !== "confirm_required") throw e;
        const subject = (e.details as { first_email_subject?: string } | undefined)?.first_email_subject ?? "";
        if (promised && subject === promised) return await api(url, { json: { ...body, confirm: true } });
        setSendCheck({ action, subject });
        return null;
      }
    },
    onSuccess: (res, { action }) => {
      if (res === null) return;
      setPending(null);
      setSendCheck(null);
      setError("");
      const done = { skip_now: "Step skipped", mark: "Step will be skipped", unmark: "Step will run as usual", move: "Moved" }[action.kind];
      toast(`${done}: ${who}`);
      for (const k of ["automation-run", "automation-positions", "automation-people", "automation", "automation-activity", "timeline"]) void qc.invalidateQueries({ queryKey: [k] });
    },
    onError: (e) => setError(errorText(e)),
  });

  const close = (o: boolean) => {
    if (!o) {
      setPending(null);
      setSendCheck(null);
      setError("");
    }
    onOpenChange(o);
  };
  const live = p && (p.status === "active" || p.status === "waiting" || p.status === "paused");
  const failed = p?.status === "failed";
  const c = pending && confirmText(pending, steps, who, !!paused);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[80vh] w-[min(680px,calc(100vw-2rem))] overflow-y-auto">
        {sendCheck ? (
          <>
            <DialogTitle>Send this email now?</DialogTitle>
            <DialogDescription>
              The next step emails {who} right away{sendCheck.subject ? `: “${sendCheck.subject}”` : ""}. This sends a real email.
            </DialogDescription>
            {error && <p className="mb-2 text-xs text-[var(--color-danger-fg)]">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSendCheck(null)}>
                Not now
              </Button>
              <Button disabled={run.isPending} onClick={() => run.mutate({ action: sendCheck.action, confirm: true })}>
                Yes, send it
              </Button>
            </div>
          </>
        ) : c && pending ? (
          <>
            <DialogTitle>{c.title}</DialogTitle>
            <DialogDescription>{c.body}</DialogDescription>
            {error && <p className="mb-2 text-xs text-[var(--color-danger-fg)]">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPending(null)}>
                Not now
              </Button>
              <Button disabled={run.isPending} onClick={() => run.mutate({ action: pending, confirm: false, promised: c.promised })}>
                {c.button}
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogTitle>Skip or move a step for {who}</DialogTitle>
            <DialogDescription>
              {p ? `${p.automation_name}. ${p.summary.replace(/^\[shadow\]\s*/, "")}` : "Loading..."}
              {p?.shadow ? " This is a shadow run: it only logs what it would do." : ""}
            </DialogDescription>
            {q.isLoading && <Skeleton className="h-32" />}
            {q.error && <p className="text-sm text-[var(--color-danger-fg)]">{errorText(q.error)}</p>}
            {p && !live && !failed && <p className="text-sm text-[var(--color-muted-foreground)]">This run has ended, so its steps can't be skipped or moved.</p>}
            {(live || failed) && (
              <ol className="space-y-1 text-sm">
                {steps.map((s) => (
                  <li key={s.key} className={cn("flex flex-wrap items-start gap-2 rounded-md border px-3 py-2", s.current && "border-[var(--color-primary)] bg-[var(--color-accent)]")}>
                    <span className="min-w-0 flex-1">
                      <span className="num mr-1.5 text-xs text-[var(--color-muted-foreground)]">{s.number}</span>
                      {s.label}
                      {s.subject && !s.label.includes(s.subject) && <span className="block text-xs text-[var(--color-muted-foreground)]">email “{s.subject}”</span>}
                      <span className="mt-0.5 flex flex-wrap gap-1">
                        {s.current && <Badge variant="success">{failed ? "stopped here" : "here now"}</Badge>}
                        {s.skip === "this_run" && <Badge variant="warning">will be skipped (set by {s.skip_by})</Badge>}
                        {s.skip === "everyone" && <Badge variant="muted">off for everyone</Badge>}
                      </span>
                    </span>
                    <span className="flex shrink-0 flex-wrap gap-1">
                      {s.current && s.skippable && (
                        <Button size="sm" variant="outline" onClick={() => setPending({ kind: "skip_now", step: s })}>
                          Skip this step
                        </Button>
                      )}
                      {!s.current && live && s.skippable && s.skip === "this_run" && (
                        <Button size="sm" variant="ghost" onClick={() => setPending({ kind: "unmark", step: s })}>
                          Don't skip
                        </Button>
                      )}
                      {!s.current && live && s.skippable && !s.skip && (
                        <Button size="sm" variant="ghost" onClick={() => setPending({ kind: "mark", step: s })}>
                          Skip when they get there
                        </Button>
                      )}
                      {!s.current && (
                        <Button size="sm" variant="ghost" onClick={() => setPending({ kind: "move", step: s })}>
                          Move here
                        </Button>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            )}
            {error && <p className="mt-2 text-xs text-[var(--color-danger-fg)]">{error}</p>}
            <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
              Skipping never runs the step. Every skip and move goes into the step log and the person's timeline. To skip a step for everyone, use the automation's page.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** A small "Skip or move" button that opens RunSkipDialog. */
export function SkipMoveButton({ runId, who, className, variant = "outline" }: { runId: number; who: string; className?: string; variant?: "outline" | "ghost" | "link" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant={variant} className={className} onClick={() => setOpen(true)} title="Skip this step, skip a later step, or move to another step">
        Skip or move
      </Button>
      {open && <RunSkipDialog runId={runId} who={who} open={open} onOpenChange={setOpen} />}
    </>
  );
}
